import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { ClockService, ColumnKey, Complaint, ComplaintAppointment, ComplaintPosition, ComplaintPriorities, ComplaintUserType, DocumentAttachment, DocumentStatus, SalesDocument, scrollTop, Workflow } from '@icc/b2b/shared';
import { ComplaintService } from '../complaint.service';
import { Router } from '@angular/router';
import { ConfigService, TranslateService } from '@icc/shared';
import { ComplaintButton } from 'libs/b2b/shared/src/lib/models/complaint-workflow-rule';
import { ToastService } from '@icc/shared/helpers';
import { DialogService } from '@icc/ui';
import { ComplaintUserPreviewDialogComponent } from '../complaint-user-preview-dialog/complaint-user-preview-dialog.component';
import { GoogleMapsLoaderService } from '../google-maps-loader.service';

@Component({
  selector: 'icc-complaint-list-table',
  templateUrl: './complaint-list-table.component.html',
  styleUrls: ['./complaint-list-table.component.scss']
})
export class ComplaintListTableComponent implements OnInit, OnChanges {
    @Output() updateDocuments = new EventEmitter<boolean>();
    @Output() saveConfiguration = new EventEmitter<void>();
    @Output() setColumnSortOptions = new EventEmitter<void>();
    @Output() changeOrderBy = new EventEmitter<ColumnKey>();

    @Input() filteredDocuments: Complaint[] = [];
    @Input() orderedDocuments: Complaint[] = [];
    @Input() viewComplaintOrderNumber = false;
    @Input() viewAssignedDistributor = false;
    @Input() viewAssignedAuthorizedRetailer = false;
    @Input() userType: ComplaintUserType = 'authorizedRetailer';
    @Input() positionsByDocument: Record<string, ComplaintPosition[]> = {};
    @Input() appointmentsByDocument: Record<string, ComplaintAppointment[]> = {};
    @Input() attachmentsByDocument: Record<string, DocumentAttachment[]> = {};

    positionsWorkflows = new Map<string, Map<string, Workflow | null>>();
    positionsWorkflowsGroups = new Map<string, Map<number, ComplaintPosition[]>>();

    correctionButtonAccess = new Map<string, boolean>();

    documentStatuses: DocumentStatus[] = [];
    showDocumentStatuses = true;

    checkedDocuments: Record<string, boolean> = {};
    showGroupOptions = false;

    documentActionsAccess = new Map<
        string,
        {
            select: boolean;
            order: boolean;
            copy: boolean;
            transferChanges: boolean;
        }
    >();

    @Input() columnSortOptions: Partial<
        Record<
            ColumnKey,
            {
                order: 'asc' | 'desc' | null;
                index: number;
            } | null
        >
    > = {};
    @Input() mode: 'selection' | 'index' = 'index';

    @Input() documentsInPage = 20;
    @Input() totalItems = 0;

    private _page = 1;
    @Input()
    get page(): number {
        return this._page;
    }
    set page(page: number) {
        this._page = page;
    }

    @Output() currentPageEmitter = new EventEmitter<number>();

    onChangePage(page: number) {
        this.page = page;
        this.currentPageEmitter.emit(page);
        this.saveConfiguration.emit();
        this.updateDocuments.emit(true);
        scrollTop();
    }

    get loaded() {
        return ClockService.getClock()?.timestamp != null;
    }

    localDocumentsResolve: () => void;
    loadedLocalDocuments: Promise<void>;

    constructor(
        private router: Router,
        private complaintService: ComplaintService,
        private translateService: TranslateService,
        private configService: ConfigService,
        private toastService: ToastService,
        private dialogService: DialogService,
        private googleMapsLoaderService: GoogleMapsLoaderService,
    ) {}

    async ngOnInit() {
        await this.complaintService.workflowsLoaded;
        this.updateCorrectionButtonAccess();
    }

    async ngOnChanges() {
        if (await this.complaintService.workflowsLoaded) {
            this.updateCorrectionButtonAccess();
        }
    }

    open(document: Complaint) {
        this.complaintService.openDocument(document);
        this.router.navigate(['trade', 'complaint', document.uuid], { queryParams: { origin: 'list' } });
    }

    orderBy(column: ColumnKey) {
        this.changeOrderBy.emit(column);
    }

    onChecked() {
        const checkedDocuments = Object.entries(this.checkedDocuments)
            .filter(([, checked]) => checked)
            .map(([uuid]) => uuid);
        this.showGroupOptions = checkedDocuments.length > 0;
    }

    hasOneStatus(documents: SalesDocument[]) {
        const firstStatus = documents[0].statusId;

        for (const document of documents) {
            if (document.statusId !== firstStatus) {
                return false;
            }
        }
        return true;
    }

    trackByUuid(index: number, item: Complaint): string {
        return item.uuid;
    }

    getPriority(document: Complaint) {
        const rawPriority = this.complaintService.getPriority(document);
        if (rawPriority) {
            const priorityGroup = ComplaintPriorities[+rawPriority.slice(0, 1)];
            const priorityValue = rawPriority.slice(1);
            return this.translateService.instant(priorityGroup) + '-' + priorityValue;
        }
        return '';
    }

    getAssignedTo(document: Complaint) {
        return this.complaintService.getAssignedTo(document, document.assignedTo, document.distributorCompanyId);
    }

    getAssignedDistributor(document: Complaint) {
        return document.distributor?.companyName || this.translateService.instant('INTERFACE|Brak');
    }

    getAssignedAuthorizedRetailer(document: Complaint) {
        return document.authorizedRetailer?.companyName
            || this.translateService.instant('INTERFACE|Brak');
    }

    getAssignedToUserName(document: Complaint) {
        return this.complaintService.getAssignedToUserName(document, document.assignedTo);
    }

    getStatus(statusId: number) {
        return this.complaintService.getStatus(statusId || 0);
    }

    getUniqueDistributorOrderNumbers(document: Complaint) {
        return document.distributorOrderNumbers;
    }

    issueCorrection(document: Complaint) {
        this.correctionButtonAccess.set(document.uuid, false);
        this.toastService.add({
            severity: 'success',
            title: this.translateService.instant('COMPLAINT|Wystawiono korektę.'),
            body: this.translateService.instant('COMPLAINT|Korekta została wystawiona.'),
        });
        this.complaintService.issueCorrection(document);
        this.workflowButtonClicked('correction_issued', document);
    }

    async updateCorrectionButtonAccess() {
        this.correctionButtonAccess.clear();

        for (const document of this.orderedDocuments) {
            if (document.correctionIssued) {
                this.correctionButtonAccess.set(document.uuid, false);
                continue;
            }

            if (this.userType !== 'manufacturer') {
                this.correctionButtonAccess.set(document.uuid, false);
                continue;
            }

            let positions: ComplaintPosition[] = [];

            if (this.positionsByDocument && this.positionsByDocument[document.uuid]) {
                positions = this.positionsByDocument[document.uuid];
            }

            if (!positions || positions.length === 0) {
                this.correctionButtonAccess.set(document.uuid, false);
                continue;
            }

            const { positionsWorkflowsGroups } = await this.complaintService.getPositionsWorkflows(document, positions);

            if (positionsWorkflowsGroups.size !== 1) {
                this.correctionButtonAccess.set(document.uuid, false);
                continue;
            }

            this.positionsWorkflowsGroups.set(document.uuid, positionsWorkflowsGroups);

            const documentStatusId = this.complaintService.getDocumentStatusForUser(document, 'manufacturer');
            const workflowId = positionsWorkflowsGroups.keys().next().value;
            const workflow = this.complaintService.workflows.find(w => +w.id === +workflowId);

            if (!workflow) {
                this.correctionButtonAccess.set(document.uuid, false);
                continue;
            }

            const manufacturerRolesStatuses = (workflow.userRolesStatuses || []).filter(
                el => el.level === 'manufacturer' &&
                el.userRolesIds.some(
                    roleId => (Number(roleId) === 0 ||
                        this.configService.user.user?.Roles.map(Number).includes(Number(roleId))
                    )
                )
            );

            let buttonAvailable = false;
            if (manufacturerRolesStatuses.length) {
                buttonAvailable = manufacturerRolesStatuses.some(
                    el => el.buttons[documentStatusId]?.correction_issued?.available
                );
            }

            this.correctionButtonAccess.set(document.uuid, buttonAvailable);
        }
    }

    workflowButtonClicked(button: ComplaintButton, document: Complaint) {
        this.complaintService.triggerWorkflow('button_click', document, {
            button,
        }, this.positionsWorkflowsGroups.get(document.uuid) || new Map(), 0, new Set());
    }

    viewProtocolsList(document: Complaint) {
        const filteredAppointments = this.appointmentsByDocument[document.uuid] || [];
        const filteredAttachments = this.attachmentsByDocument[document.uuid] || [];
        this.complaintService.viewProtocolsList(document, filteredAppointments, filteredAttachments);
    }

    complaintHasProtocols(document: Complaint) {
        return this.appointmentsByDocument[document.uuid]?.length > 0;
    }

    async openLocationDialog(document: Complaint) {
        await this.complaintService.loaded;
        const apiKey = this.complaintService.data?.googleMapsApiKey;
        if (apiKey) {
            await this.googleMapsLoaderService.load(apiKey);
        }
        const apiLoaded = this.googleMapsLoaderService.isApiLoaded();

        const user = {
            referenceName: document.clientName || '',
            email: document.clientEmail || '',
            phoneNumber: document.clientPhoneNumber || '',
            address: document.installationAddress || '',
        };

        this.dialogService.open(ComplaintUserPreviewDialogComponent, {
            data: {
                user,
                userType: 'client',
                responsibleUser: null,
                assignedUser: null,
                locationDescription: document.locationDescription,
                locationSource: document.locationSource,
                locationPartialMatchConfirmed: document.locationPartialMatchConfirmed,
                latitude: document.latitude,
                longitude: document.longitude,
                apiLoaded,
            },
        });
    }}
